import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import Landing from './pages/Landing'

// Every other route is lazy, it pulls in its own set of components/images, none of
// which a visitor landing directly on /login or /pricing needs. Each route only costs
// its own chunk fetch once it's actually the one being visited. Landing is the "/" route
// almost every visitor hits, and it statically imports AskAnythingBar (which itself
// imports the api client), both shared with several /app pages, so Rollup splits them
// into their own chunks regardless. Behind lazy(), that turned into a serial chain
// (index.js -> Landing.js -> AskAnythingBar.js -> api.js) before the homepage could
// render at all. Importing Landing eagerly puts it back on the entry's static dependency
// graph, so Vite emits modulepreload hints for those chunks and the browser fetches them
// in parallel with index.js instead of discovering each one only after the last finishes.
const Pricing = lazy(() => import('./pages/Pricing'))
const Login = lazy(() => import('./pages/Login'))
const AppLayout = lazy(() => import('./pages/app/AppLayout'))
const DashboardHome = lazy(() => import('./pages/app/DashboardHome'))
const Piq = lazy(() => import('./pages/app/Piq'))
const FullPsychSetup = lazy(() => import('./pages/app/FullPsychSetup'))
const FullPsychTest = lazy(() => import('./pages/app/FullPsychTest'))
const TatSetup = lazy(() => import('./pages/app/TatSetup'))
const TatTest = lazy(() => import('./pages/app/TatTest'))
const WatSetup = lazy(() => import('./pages/app/WatSetup'))
const WatTest = lazy(() => import('./pages/app/WatTest'))
const SrtSetup = lazy(() => import('./pages/app/SrtSetup'))
const SrtTest = lazy(() => import('./pages/app/SrtTest'))
const SdtSetup = lazy(() => import('./pages/app/SdtSetup'))
const SdtTest = lazy(() => import('./pages/app/SdtTest'))
const BsfSetup = lazy(() => import('./pages/app/BsfSetup'))
const BsfTest = lazy(() => import('./pages/app/BsfTest'))
const PpdtSetup = lazy(() => import('./pages/app/PpdtSetup'))
const PpdtTest = lazy(() => import('./pages/app/PpdtTest'))
const GpeSetup = lazy(() => import('./pages/app/GpeSetup'))
const GpeTest = lazy(() => import('./pages/app/GpeTest'))
const GdSetup = lazy(() => import('./pages/app/GdSetup'))
const GdTest = lazy(() => import('./pages/app/GdTest'))
const LecturetteSetup = lazy(() => import('./pages/app/LecturetteSetup'))
const LecturetteTest = lazy(() => import('./pages/app/LecturetteTest'))
const OirSetup = lazy(() => import('./pages/app/OirSetup'))
const OirTest = lazy(() => import('./pages/app/OirTest'))
const InterviewSetup = lazy(() => import('./pages/app/InterviewSetup'))
const InterviewTest = lazy(() => import('./pages/app/InterviewTest'))
const Report = lazy(() => import('./pages/app/Report'))

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />

          <Route path="/app" element={<AppLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="piq" element={<Piq />} />
            <Route path="full-psych" element={<FullPsychSetup />} />
            <Route path="full-psych/test" element={<FullPsychTest />} />
            <Route path="tat" element={<TatSetup />} />
            <Route path="tat/test" element={<TatTest />} />
            <Route path="wat" element={<WatSetup />} />
            <Route path="wat/test" element={<WatTest />} />
            <Route path="srt" element={<SrtSetup />} />
            <Route path="srt/test" element={<SrtTest />} />
            <Route path="sdt" element={<SdtSetup />} />
            <Route path="sdt/test" element={<SdtTest />} />
            <Route path="bsf" element={<BsfSetup />} />
            <Route path="bsf/test" element={<BsfTest />} />
            <Route path="ppdt" element={<PpdtSetup />} />
            <Route path="ppdt/test" element={<PpdtTest />} />
            <Route path="gpe" element={<GpeSetup />} />
            <Route path="gpe/test" element={<GpeTest />} />
            <Route path="gd" element={<GdSetup />} />
            <Route path="gd/test" element={<GdTest />} />
            <Route path="lecturette" element={<LecturetteSetup />} />
            <Route path="lecturette/test" element={<LecturetteTest />} />
            <Route path="oir" element={<OirSetup />} />
            <Route path="oir/test" element={<OirTest />} />
            <Route path="interview" element={<InterviewSetup />} />
            <Route path="interview/test" element={<InterviewTest />} />
            <Route path="report" element={<Report />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
